import Avatar from '@precis-digital/kurama/Avatar'
import { type Theme } from '@precis-digital/kurama/theme'
import { styled } from '@precis-digital/kurama/utils'
import React, { useState } from 'react'
import type { ClientResp } from 'shared/api/clients'
import { useCurrentClient, type Client } from 'shared/context/ClientContext'
import { getClientAvatarUrl, getUserAvatarUrl } from 'shared/assetsBucket'
import ThemedAvatar from 'shared/components/Themed/Avatar'
import { type Member } from 'src/workspace/api'

type AvatarLabelProps = React.ComponentProps<typeof Avatar>
type AvatarUser = Member | { name: string; avatarUrl?: string } | undefined
type AvatarClient = Omit<Client, 'featurePlan' | 'currency'> | ClientResp | undefined

type UserAvatarProps = Omit<AvatarLabelProps, 'label' | 'kind'> & {
  user?: AvatarUser
  client?: AvatarClient
  handleOnImageError?: () => void
  avatarVersion?: number
}

export const AvatarWithLabel = (props: UserAvatarProps): React.ReactElement => {
  const { user, client, handleOnImageError, avatarVersion, ...other } = props
  const [hasAvatarClientImageError, setHasAvatarClientImageError] = useState(false)
  const [hasProxyUserImageError, setHasProxyUserImageError] = useState(false)
  const [hasAvatarUrlError, setHasAvatarUrlError] = useState(false)

  const { isRefetching } = useCurrentClient()
  const userId = user !== undefined && 'id' in user ? user.id : undefined

  React.useEffect(() => {
    if (client != null && hasAvatarClientImageError && isRefetching) {
      setHasAvatarClientImageError(false)
    }
  }, [isRefetching, hasAvatarClientImageError, client])

  React.useEffect(() => {
    setHasProxyUserImageError(false)
    setHasAvatarUrlError(false)
  }, [userId])

  const bust = avatarVersion != null ? `?v=${avatarVersion}` : ''

  if (client?.id !== undefined && !hasAvatarClientImageError) {
    return (
      <ThemedAvatar
        {...other}
        kind="image"
        imageUrl={`${getClientAvatarUrl(client.id)}${bust}`}
        objectFit="contain"
        onError={() => {
          setHasAvatarClientImageError(true)
          handleOnImageError?.()
        }}
      />
    )
  }

  if (userId !== undefined && !hasProxyUserImageError) {
    return (
      <ThemedAvatar
        {...other}
        kind="image"
        imageUrl={`${getUserAvatarUrl(userId)}${bust}`}
        objectFit="contain"
        onError={() => {
          setHasProxyUserImageError(true)
        }}
      />
    )
  }

  if (user?.avatarUrl != null && user.avatarUrl !== '' && !hasAvatarUrlError) {
    return (
      <ThemedAvatar
        {...other}
        kind="image"
        imageUrl={user.avatarUrl}
        objectFit="contain"
        onError={() => {
          setHasAvatarUrlError(true)
        }}
      />
    )
  }

  const initials = user?.name != null ? getUserLabel(user) : getClientLabel(client)
  const labelColor = (user?.name != null ? user.name : client?.name) ?? initials

  return <AvatarColor label={initials} $labelColor={labelColor} kind="label" {...other} />
}

const getClientLabel = (client: AvatarClient): string => {
  return typeof client?.name === 'string' && client.name.length > 0 ? client.name[0] : ''
}

const getUserLabel = (user: AvatarUser): string => {
  return user?.name != null
    ? user.name
        .split(' ')
        .map((word) => {
          return word[0]
        })
        .join('')
    : ''
}

const DATAVIZ_COLOR_INDEXES = [1, 2, 3, 4, 5, 6, 7, 8] as const

export const labelColors = (theme: Theme): string[] =>
  DATAVIZ_COLOR_INDEXES.map((index) => theme.tokens.dataViz.primary[index].fill.subtle)

export const getLabelColor = (theme: Theme, label: string): string => {
  const colors = labelColors(theme)
  const colorIndex = (label?.charCodeAt(0) + label?.charCodeAt(label?.length - 1)) % colors.length
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  return colors[colorIndex || 0]
}

const AvatarColor = styled(Avatar)<{ $labelColor: string }>(({ theme, $labelColor: labelColor }) => {
  const backgroundColor = getLabelColor(theme, labelColor)
  return {
    background: backgroundColor,
    p: {
      // TODO: replace with theme.getContrastText() once Kurama exposes a token-aware helper
      // eslint-disable-next-line no-restricted-syntax -- no token-aware contrast helper yet
      color: theme.palette.getContrastText(backgroundColor)
    }
  }
})
